import { useCallback, useEffect, useState } from 'react';
import { avisar, avisarErro } from '../componentes/Avisos.tsx';
import { AutorizacoesDaEster } from '../componentes/AutorizacoesDaEster.tsx';
import { FormularioCompromisso } from '../componentes/FormularioCompromisso.tsx';
import { ItemCompromisso } from '../componentes/ItemCompromisso.tsx';
import { ErroDaApi, api, type Compromisso, type Painel } from '../lib/api.ts';
import { Icone } from '../componentes/Icone.tsx';
import { porExtenso } from '../lib/datas.ts';
import '../estilos/calendario.css';
import '../estilos/sistema.css';
import '../estilos/hoje.css';

/**
 * A tela de abertura do Hub, alcançada pelo logo.
 *
 * **Só o dia de hoje, mais o que ficou para trás.** Atrasado não é "outro dia":
 * é trabalho de hoje que devia ter acontecido antes. Esconder isso da agenda do
 * dia seria a única forma de o prazo passar sem ninguém ver.
 *
 * O que vem depois de hoje mora no Calendário, que é a aba ao lado.
 */
export function Hoje({
  aoExpirarSessao,
  aoAbrirCalendario,
}: {
  aoExpirarSessao: () => void;
  aoAbrirCalendario: () => void;
}) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [formulario, setFormulario] = useState<
    { modo: 'novo' } | { modo: 'editar'; alvo: Compromisso } | null
  >(null);

  const tratar = useCallback(
    (e: unknown) => {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro(e);
      setErro(e instanceof Error ? e.message : 'Algo não funcionou.');
    },
    [aoExpirarSessao],
  );

  const recarregar = useCallback(async () => {
    try {
      setErro(null);
      setPainel(await api.painel());
    } catch (e) {
      tratar(e);
    }
  }, [tratar]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function agir(acao: () => Promise<unknown>) {
    try {
      await acao();
      await recarregar();
    } catch (e) {
      tratar(e);
    }
  }

  if (!painel) return <div className="carregando">{erro ?? 'Carregando…'}</div>;

  const deHoje = painel.proximos.filter((c) => c.data === painel.hoje);
  const nada = deHoje.length === 0 && painel.atrasados.length === 0;

  const acoes = (c: Compromisso) => ({
    aoConcluir: () => agir(() => api.concluir(c.id)),
    aoDesmarcar: () => agir(() => api.desmarcar(c.id)),
    aoEditar: () => setFormulario({ modo: 'editar', alvo: c }),
    aoApagar: () => agir(() => api.apagar(c.id)),
  });

  const feitos = deHoje.filter((c) => c.concluido).length;

  return (
    /*
      ⭐ **Sistema visual 2026, parte 4 — a tela Hoje, 14/09/2026.** A mesma
      régua da ficha e do relatório: a folha de identidade no alto e uma folha
      para cada lista. ⛔ **Mudou a aparência, não o que se clica:** concluir,
      desmarcar, editar, apagar e anotar são os mesmos, e continua valendo
      "só o dia de hoje, mais o que ficou para trás".
    */
    <div className="hoje-2026">
      {erro && <p className="erro">{erro}</p>}

      <section className="s-folha s-identidade hoje-identidade">
        <div className="s-monograma hoje-monograma" aria-hidden="true">
          <Icone nome="hoje" tamanho={28} />
        </div>
        <div className="hoje-identidade-texto">
          <span className="s-sobrelinha">Agenda do dia</span>
          <h1 className="titulo">Hoje</h1>
          <p className="hoje-data">{porExtenso(painel.hoje)}</p>
        </div>
        <button
          type="button"
          className="botao-primario s-botao"
          onClick={() => setFormulario({ modo: 'novo' })}
        >
          <Icone nome="mais" tamanho={15} />
          Anotar compromisso
        </button>
      </section>

      {/* ⭐ PRD §60: o que a Ester pede para fazer com o seu sim. Some quando não há pedido. */}
      <AutorizacoesDaEster />

      {painel.atrasados.length > 0 && (
        <section className="s-folha hoje-lista hoje-atrasado" aria-labelledby="hoje-atrasado">
          <div className="s-folha-topo hoje-lista-topo">
            <h2 id="hoje-atrasado" className="s-folha-titulo">
              Atrasado
            </h2>
            <span className="s-pilula s-pilula-vinho">{painel.atrasados.length}</span>
          </div>
          <div className="hoje-itens">
            {painel.atrasados.map((c) => (
              <ItemCompromisso key={c.id} compromisso={c} atrasado desenho="linha" {...acoes(c)} />
            ))}
          </div>
        </section>
      )}

      <section className="s-folha hoje-lista" aria-labelledby="hoje-do-dia">
        <div className="s-folha-topo hoje-lista-topo">
          <h2 id="hoje-do-dia" className="s-folha-titulo">
            Do dia
          </h2>
          {/* "0 de 2 feitos" sem fazer conta — a mesma barra em segmentos da ficha. */}
          {deHoje.length > 0 && (
            <span className="hoje-avanco">
              <span className="s-segmentos" aria-hidden="true">
                {deHoje.map((c) => (
                  <i key={c.id} className={c.concluido ? 'cheio' : undefined} />
                ))}
              </span>
              <span>
                <strong>{feitos}</strong> de {deHoje.length} feitos
              </span>
            </span>
          )}
        </div>
        <div className="hoje-itens">
          {deHoje.length === 0 ? (
            <p className="vazio">Nada marcado para hoje.</p>
          ) : (
            deHoje.map((c) => (
              <ItemCompromisso key={c.id} compromisso={c} semData desenho="linha" {...acoes(c)} />
            ))
          )}
          {nada && (
            <p className="ajuda ajuda-solta">
              O que vem depois está no{' '}
              <button type="button" className="botao-texto ligacao" onClick={aoAbrirCalendario}>
                Calendário
              </button>
              .
            </p>
          )}
        </div>
      </section>

      {formulario && (
        <FormularioCompromisso
          inicial={formulario.modo === 'editar' ? formulario.alvo : null}
          dataSugerida={formulario.modo === 'novo' ? painel.hoje : undefined}
          aoFechar={() => setFormulario(null)}
          aoSalvar={async (dados) => {
            if (formulario.modo === 'editar') {
              await api.editar(formulario.alvo.id, dados);
              avisar('Compromisso atualizado.');
            } else {
              await api.criar(dados);
              avisar('Compromisso anotado.');
            }
            setFormulario(null);
            await recarregar();
          }}
        />
      )}
    </div>
  );
}
